"use client";

import { useState, useRef, useCallback } from "react";

export interface RewrittenNode {
  id: string;
  text: string;
  translation: string;
  vocalizationTip: string;
}

export interface RewriteParams {
  fixedNodeId: string;
  originalText: string;
  originalTranslation: string;
  fixedText: string;
  fixedTranslation: string;
  userIntent: string;
  targetLanguage: string;
  nativeLanguage: string;
  dialogueNodes: { id: string; type: string; text: string; translation: string; children?: { id: string }[] }[];
  knownLanguages?: string[];
}

export function useNodeRewrite() {
  const [isRewriting, setIsRewriting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const rewrite = useCallback(
    async (params: RewriteParams, onNode: (node: RewrittenNode) => void) => {
      // Cancel any in-flight rewrite
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsRewriting(true);

      const base = process.env.NEXT_PUBLIC_API_URL || "";
      const pw = typeof window !== "undefined" ? sessionStorage.getItem("saysay-auth") : null;

      try {
        const response = await fetch(`${base}/api/rewrite-nodes`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(pw ? { "x-app-password": pw } : {}),
          },
          body: JSON.stringify(params),
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          setIsRewriting(false);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let currentEvent = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            if (buffer.trim()) processLines(buffer.split("\n"));
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          processLines(lines);
        }

        function processLines(lines: string[]) {
          for (const line of lines) {
            if (line.startsWith("event: ")) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              const dataStr = line.slice(6);
              try {
                const data = JSON.parse(dataStr);
                if (currentEvent === "rewritten_node") {
                  onNode(data as RewrittenNode);
                }
              } catch {
                // skip unparseable data
              }
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          console.error("Node rewrite error:", err);
        }
      } finally {
        if (abortRef.current === controller) {
          setIsRewriting(false);
          abortRef.current = null;
        }
      }
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsRewriting(false);
  }, []);

  return { rewrite, isRewriting, cancel };
}
